import { ConnectorRequestsComponent } from './connector-requests.component';
import { ConnectorLinkDialogComponent } from './connector-link-dialog.component';
import { OrganisationConnectorService, ConnectorAssignment } from './organisation-connector.service';
import { firstValueFrom } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { filter, finalize, take } from 'rxjs/operators';
import {
  TableColumn,
  TableEmptyState,
  TablePageAction,
  TablePageActionEvent,
  TablePageComponent,
  TableRowAction,
  TableRowActionEvent,
  TableSummaryItem
} from '../table/table-page.component';
import {
  SaasStack,
  SaasInstanceDiscoveryService
} from './saas-instance-discovery.service';
import {
  SaasStackCreateDialogComponent,
  SaasStackCreateDialogResult
} from './saas-stack-create-dialog.component';
import {
  SaasStackDeleteDialogComponent,
  SaasStackDeleteDialogData
} from './saas-stack-delete-dialog.component';

@Component({
  selector: 'app-saas-service-management',
  standalone: true,
  imports: [
    ConnectorRequestsComponent,
    MatDialogModule,
    MatSnackBarModule,
    TablePageComponent
  ],
  templateUrl: './saas-service-management.component.html',
  styleUrls: ['./saas-service-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SaasServiceManagementComponent implements OnInit {
  actions: TablePageAction[] = [
    { id: 'create', label: 'Neue Instanz', icon: 'add', appearance: 'flat', tone: 'primary' }
  ];

  readonly columns: readonly TableColumn<SaasStack>[] = [
    { key: 'organisation', label: 'Organisation', value: row => this.assignments.find(a => a.instanceId === row.name)?.organisationName || 'Nicht verknüpft' },
    {
      key: 'name',
      label: 'Docker-Stack',
      secondaryValue: () => 'Container anzeigen'
    }
  ];

  readonly rowActions: readonly TableRowAction<SaasStack>[] = [
    { id: 'link', label: 'Organisation verknüpfen', icon: 'link', disabled: () => this.creatingStack || this.deletingStackName !== null },
    {
      id: 'delete',
      label: 'Instanz löschen',
      icon: 'delete',
      tone: 'warn',
      disabled: () => this.creatingStack || this.deletingStackName !== null,
      loading: stack => this.deletingStackName === stack.name
    }
  ];

  readonly summaryItems: readonly TableSummaryItem<SaasStack>[] = [
    { label: 'Docker-Stacks', value: rows => rows.length, icon: 'layers' }
  ];

  readonly emptyState: TableEmptyState = {
    icon: 'cloud_off',
    title: 'Keine Docker-Stacks entdeckt',
    description: 'Die n8n-Discovery hat aktuell keine Docker-Stacks gemeldet.'
  };

  assignments: ConnectorAssignment[] = [];
  stacks: SaasStack[] = [];
  selectedStack: SaasStack | null = null;
  loading = true;
  refreshing = false;
  errorMessage = '';
  lastDiscovery: Date | null = null;
  creatingStack = false;
  deletingStackName: string | null = null;

  constructor(
    private readonly connectorApi: OrganisationConnectorService,
    private readonly http: HttpClient,
    private readonly discoveryService: SaasInstanceDiscoveryService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    this.loadStacks(false);
  }

  get description(): string {
    const timestamp = this.lastDiscovery
      ? ` Letzte Discovery: ${new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' }).format(this.lastDiscovery)} Uhr.`
      : '';
    return `Docker-Stacks auf der SaaS-Bundle-VM. Wählen Sie einen Stack, um dessen Container zu öffnen.${timestamp}`;
  }

  openStack(stack: SaasStack | null): void {
    if (!stack) return;
    void this.router.navigate(['/profil/saas-services', stack.name]);
  }

  refresh(): void {
    if (this.loading || this.refreshing) return;
    this.loadStacks(true);
  }

  retry(): void {
    this.loadStacks(false);
  }

  handlePageAction(event: TablePageActionEvent): void {
    if (event.actionId !== 'create' || this.creatingStack || this.deletingStackName !== null) return;

    this.dialog.open(SaasStackCreateDialogComponent, {
      width: '34rem',
      maxWidth: 'calc(100vw - 2rem)'
    }).afterClosed().pipe(
      take(1),
      filter((result): result is SaasStackCreateDialogResult => Boolean(result))
    ).subscribe(result => this.createStack(result));
  }

  handleRowAction(event: TableRowActionEvent<SaasStack>): void {
    if (event.actionId === 'link') { void this.linkOrganisation(event.row); return; }
    if (event.actionId !== 'delete' || this.creatingStack || this.deletingStackName !== null) return;

    const data: SaasStackDeleteDialogData = { stackName: event.row.name };
    this.dialog.open(SaasStackDeleteDialogComponent, {
      width: '34rem',
      maxWidth: 'calc(100vw - 2rem)',
      data
    }).afterClosed().pipe(
      take(1),
      filter((token): token is string => typeof token === 'string' && token.trim().length > 0)
    ).subscribe(token => this.deleteStack(event.row, token));
  }

  private async linkOrganisation(stack: SaasStack): Promise<void> {
    try {
      const organisations=await firstValueFrom(this.http.get<any[]>('/proxy/api/v0/userservice/organisations'));
      const assignment=this.assignments.find(a=>a.instanceId===stack.name);
      const result=await firstValueFrom(this.dialog.open(ConnectorLinkDialogComponent,{width:'660px',maxWidth:'96vw',data:{mode:'link',organisations,assignment}}).afterClosed());
      if(!result)return;
      this.creatingStack=true;this.updatePageActionState();this.changeDetectorRef.markForCheck();
      await firstValueFrom(this.connectorApi.link(stack.name,result));
      this.assignments=await firstValueFrom(this.connectorApi.list());
      this.snackBar.open('Organisation und SaaS-Instanz wurden verknüpft.','Schließen',{duration:4000});
    } catch(error) {this.snackBar.open(this.errorText(error),'Schließen',{duration:6000});}
    finally{this.creatingStack=false;this.updatePageActionState();this.changeDetectorRef.markForCheck();}
  }

  private createStack(result: SaasStackCreateDialogResult): void {
    this.creatingStack = true;
    this.updatePageActionState();

    this.discoveryService.createStack(result).pipe(
      finalize(() => {
        this.creatingStack = false;
        this.updatePageActionState();
        this.changeDetectorRef.markForCheck();
      })
    ).subscribe({
      next: () => {
        this.snackBar.open(`SaaS-Instanz für ${result.participantName} wird erstellt.`, 'Schließen', { duration: 4000 });
        this.loadStacks(true);
      },
      error: error => this.snackBar.open(this.errorText(error), 'Schließen', { duration: 6000 })
    });
  }

  private deleteStack(stack: SaasStack, pb40Token: string): void {
    this.deletingStackName = stack.name;
    this.updatePageActionState();
    this.changeDetectorRef.markForCheck();

    this.discoveryService.deleteStack(stack.name, pb40Token).pipe(
      finalize(() => {
        this.deletingStackName = null;
        this.updatePageActionState();
        this.changeDetectorRef.markForCheck();
      })
    ).subscribe({
      next: () => {
        this.selectedStack = null;
        this.snackBar.open(`SaaS-Instanz ${stack.name} wurde gelöscht.`, 'Schließen', { duration: 4000 });
        this.loadStacks(true);
      },
      error: error => this.snackBar.open(this.errorText(error), 'Schließen', { duration: 6000 })
    });
  }

  private updatePageActionState(): void {
    this.actions = this.actions.map(action => action.id === 'create'
      ? { ...action, loading: this.creatingStack, disabled: this.creatingStack || this.deletingStackName !== null }
      : action);
  }

  private loadStacks(isRefresh: boolean): void {
    this.connectorApi.list().subscribe({next: rows => { this.assignments=rows; this.changeDetectorRef.markForCheck(); }, error: () => {} });
    this.errorMessage = '';
    this.loading = !isRefresh;
    this.refreshing = isRefresh;

    this.discoveryService.discoverStacks().pipe(
      finalize(() => {
        this.loading = false;
        this.refreshing = false;
        this.changeDetectorRef.markForCheck();
      })
    ).subscribe({
      next: stacks => {
        this.stacks = stacks;
        this.lastDiscovery = new Date();
        if (isRefresh) {
          this.snackBar.open(`${stacks.length} Docker-Stacks aktualisiert.`, 'Schließen', { duration: 3500 });
        }
      },
      error: error => {
        this.errorMessage = this.errorText(error);
      }
    });
  }

  private errorText(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      if (typeof error.error === 'string' && error.error.trim()) return error.error;
      if (error.error && typeof error.error === 'object' && typeof error.error.message === 'string') {
        return error.error.message;
      }
      if (error.status === 0) {
        return 'Die n8n-Discovery ist nicht erreichbar. Prüfen Sie Workflow-Testmodus, Netzwerk und Proxy-Konfiguration.';
      }
    }
    if (error instanceof Error && error.message) return error.message;
    return 'Die Docker-Stacks konnten nicht geladen werden.';
  }
}
